const TOOLTIP_VISIBLE_MS = 2000;

const MESSAGES = {
	success: 'Link copied',
	error: 'Copy failed',
};

/**
 * Build the tooltip element used to confirm the copy action.
 *
 * It doubles as an ARIA live region: created once (empty) at init so the
 * change in its text content is reliably announced by assistive tech.
 *
 * @return {HTMLSpanElement} The tooltip element.
 */
function createTooltip() {
	const tooltip = document.createElement('span');
	tooltip.className = 'gt-copy-link__tooltip';
	tooltip.setAttribute('role', 'status');
	tooltip.setAttribute('aria-live', 'polite');
	tooltip.setAttribute('aria-atomic', 'true');
	return tooltip;
}

/**
 * Resolve the element that should act as the interactive trigger.
 *
 * `data-copy-link` may live on a non-interactive element (e.g. a <span>) nested
 * inside a <button>. Keyboard activation fires on the focusable ancestor,
 * not its descendants, so the behaviour must be bound there. When no native
 * control wraps the element, the element itself is promoted to a button so it
 * is focusable and operable by keyboard and assistive tech.
 *
 * @param {HTMLElement} source The element carrying the data-copy-link attribute.
 * @return {{ trigger: HTMLElement, isNativeControl: boolean }} object
 */
function resolveTrigger(source) {
	const nativeControl = source.closest('button');
	if (nativeControl) {
		return { trigger: nativeControl, isNativeControl: true };
	}

	source.setAttribute('role', 'button');
	source.setAttribute('tabindex', '0');
	return { trigger: source, isNativeControl: false };
}

/**
 * Initializes the copy link functionality.
 *
 * @param {HTMLElement} source The element carrying the data-copy-link attribute.
 * @return {void}
 */
function initCopyLink(source) {
	// Guard against double-initialization. Bricks AJAX re-renders (filtering,
	// "Show more") re-run init over the whole document, which would otherwise
	// append a second tooltip and double-bind the listeners on existing
	// triggers. Already-wired sources are skipped; only fresh nodes are wired.
	if (source.dataset.copyLinkBound === '1') {
		return;
	}
	source.dataset.copyLinkBound = '1';

	const { trigger, isNativeControl } = resolveTrigger(source);

	trigger.classList.add('gt-copy-link');

	const tooltip = createTooltip();
	trigger.appendChild(tooltip);

	let hideTimer;

	const showTooltip = (message) => {
		window.clearTimeout(hideTimer);
		tooltip.textContent = message;
		tooltip.classList.add('gt-copy-link__tooltip--visible');

		hideTimer = window.setTimeout(() => {
			tooltip.classList.remove('gt-copy-link__tooltip--visible');
			tooltip.textContent = '';
		}, TOOLTIP_VISIBLE_MS);
	};

	const copyLink = async (e) => {
		const link = source.getAttribute('data-copy-link');
		if (!link) {
			return;
		}

		e.preventDefault();

		try {
			await navigator.clipboard.writeText(link);
			showTooltip(MESSAGES.success);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('Copy failed!', err);
			showTooltip(MESSAGES.error);
		}
	};

	trigger.addEventListener('click', copyLink);

	if (isNativeControl) {
		return;
	}

	// The promoted role="button" fallback must replicate native button keyboard
	// behaviour: activate on Enter (keydown) and Space (keyup), and suppress the
	// page-scroll that Space would otherwise trigger.
	trigger.addEventListener('keydown', (e) => {
		if (e.key === 'Enter') {
			copyLink(e);
		} else if (e.key === ' ') {
			e.preventDefault();
		}
	});

	trigger.addEventListener('keyup', (e) => {
		if (e.key === ' ') {
			copyLink(e);
		}
	});
}

/**
 * Wire up every uninitialized [data-copy-link] element currently in the DOM.
 *
 * @return {void}
 */
function initAllCopyLinks() {
	document.querySelectorAll('[data-copy-link]').forEach(initCopyLink);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAllCopyLinks);
} else {
	initAllCopyLinks();
}

// Bricks injects new query-result nodes (and removes old ones) on AJAX
// filtering, "Show more", and pagination without a page reload, so those
// fresh triggers never hit DOMContentLoaded. Re-run on the Bricks AJAX
// lifecycle events; the per-element guard keeps existing triggers untouched.
[
	'bricks/ajax/query_result/displayed',
	'bricks/ajax/load_page/completed',
	'bricks/ajax/pagination/completed',
].forEach((event) => {
	document.addEventListener(event, initAllCopyLinks);
});
